/**
 * Reusable targeting/negatives/search-terms/AI-actions tables — shared between
 * the campaign overview (`$id.index.tsx`) and the ad-group detail page
 * (`$id.ad-groups.$adGroupId.tsx`) since ADP-48 relocated ad-group content.
 *
 * Extracted verbatim from `$id.index.tsx`; the overview keeps its own
 * CampaignHeader / StrategyCard / PerformanceTrend / AdGroupsSection / skeleton
 * (those are overview-only).
 */
import type {
  CampaignAiAction,
  CampaignDetailRange,
  CampaignNegativeTarget,
  CampaignSearchTerm,
  CampaignTarget,
  TargetType,
} from "@adpilot/shared";
import { expressionTypeLabel } from "@adpilot/shared";
import {
  IconArrowsUpDown,
  IconArrowUp,
  IconBrain,
  IconDeviceFloppy,
  IconPlus,
  IconTag,
} from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { FilterSelect } from "@/components/patterns/filter-select";
import { SearchInput } from "@/components/patterns/SearchInput";
import { EmptyState } from "@/components/shared/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getApiErrorMessage } from "@/lib/api-error";
import { updateTargetTags } from "@/lib/campaign-api";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";

/** Range values only — render labels via `t(\`campaign:range.${value}\`)`. */
export const RANGE_OPTIONS: CampaignDetailRange[] = ["yesterday", "7d", "30d"];

export type SortKey =
  | "label"
  | "matchType"
  | "adGroup"
  | "bid"
  | "spend"
  | "sales"
  | "acos"
  | "orders"
  | "cvr"
  | "cpc";
export type SortDir = "asc" | "desc";

// Filter option order lives at module scope; labels resolve at render via
// `t("targets.typeFilter.*")` / `t("searchTerms.outcomeFilter.*")`.
const TARGET_TYPE_FILTER_VALUES: readonly ("all" | TargetType)[] = [
  "all",
  "keyword",
  "product",
  "auto",
  "product_category",
  "theme",
];

type SearchTermOutcome = "all" | CampaignSearchTerm["aiSuggestion"];

const SEARCH_TERM_OUTCOME_VALUES: readonly SearchTermOutcome[] = [
  "all",
  "harvest",
  "negative",
  "observe",
];

/**
 * Positive-targets table for ONE ad group (ADP-48). The ADP-44 per-ad-group
 * Accordion grouping is gone — every caller is already scoped to an ad group,
 * so grouping would be dead code. `showAdGroupColumn` is kept false at ad-group
 * scope (the column is redundant) and unused on the overview.
 */
function TargetsTableInner({
  campaignId,
  targets,
}: {
  campaignId: string;
  targets: CampaignTarget[];
}) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatPercent, formatNumber } = useFormatters();
  const queryClient = useQueryClient();
  const [sortKey, setSortKey] = useState<SortKey>("spend");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [editingTargetId, setEditingTargetId] = useState<string | null>(null);
  const [draftTags, setDraftTags] = useState("");
  const [query, setQuery] = useState("");
  const [targetType, setTargetType] = useState<"all" | TargetType>("all");
  const [tagFilterOpen, setTagFilterOpen] = useState(false);
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [draftTagFilters, setDraftTagFilters] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const mutation = useMutation({
    mutationFn: ({ targetId, tags }: { targetId: string; tags: string[] }) =>
      updateTargetTags(targetId, tags),
    onSuccess: () => {
      toast.success(t("toasts.tagsUpdated"));
      setEditingTargetId(null);
      void queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
    },
    onError: async (err) => toast.error(await getApiErrorMessage(err)),
  });

  const availableTags = useMemo(
    () => [...new Set(targets.flatMap((target) => target.tags.map((tag) => tag.tag)))].sort(),
    [targets],
  );
  useEffect(() => {
    const availableTagSet = new Set(availableTags);
    const pruneMissingTags = (current: string[]) => {
      const next = current.filter((tag) => availableTagSet.has(tag));
      return next.length === current.length ? current : next;
    };

    setTagFilters(pruneMissingTags);
    setDraftTagFilters(pruneMissingTags);
  }, [availableTags]);
  const sorted = useMemo(() => {
    return targets
      .filter(
        (target) =>
          target.label.toLowerCase().includes(query.toLowerCase()) &&
          (targetType === "all" || target.targetType === targetType) &&
          (tagFilters.length === 0 || target.tags.some((tag) => tagFilters.includes(tag.tag))),
      )
      .sort((a, b) => {
        const av = targetSortValue(a, sortKey);
        const bv = targetSortValue(b, sortKey);
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortDir === "asc" ? cmp : -cmp;
      });
  }, [targets, query, sortKey, sortDir, targetType, tagFilters]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const visible = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("desc");
  }

  function startEdit(target: CampaignTarget) {
    setEditingTargetId(target.id);
    setDraftTags(target.tags.map((tag) => tag.tag).join(", "));
  }

  function saveTags(targetId: string) {
    const tags = draftTags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    mutation.mutate({ targetId, tags });
  }

  return (
    <DataTableFrame
      toolbar={
        <>
          <SearchInput
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder={t("targets.searchPlaceholder")}
            textSize="compact"
            className="w-[210px]"
          />
          <div className="flex items-center gap-2">
            <FilterSelect
              ariaLabel={t("targets.filterTypeAria")}
              items={TARGET_TYPE_FILTER_VALUES.map((value) => ({
                value,
                label: t(`targets.typeFilter.${value}`),
              }))}
              value={targetType}
              onValueChange={(next) => {
                setTargetType(next as "all" | TargetType);
                setPage(1);
              }}
            />
            <Popover
              open={tagFilterOpen}
              onOpenChange={(open) => {
                setTagFilterOpen(open);
                if (open) setDraftTagFilters(tagFilters);
              }}
            >
              <PopoverTrigger
                render={
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={t("targets.filterByTagAria")}
                    data-filter-applied={tagFilters.length > 0 || undefined}
                    disabled={availableTags.length === 0 && tagFilters.length === 0}
                    className={cn(
                      "h-8 gap-1.5 text-xs",
                      tagFilters.length > 0 && "border-transparent bg-control-highlight",
                    )}
                  />
                }
              >
                <IconTag className="size-3.5" />
                {t("targets.table.tags")}
                {tagFilters.length > 0 ? ` · ${tagFilters.length}` : ""}
              </PopoverTrigger>
              <PopoverPopup
                align="end"
                sideOffset={8}
                initialFocus={false}
                className="w-[248px] overflow-hidden rounded-[10px] p-0 [&>[data-slot=popover-viewport]]:p-0"
              >
                <div className="border-b border-line px-3.5 py-3">
                  <p className="text-xs font-semibold text-foreground">
                    {t("targets.filterByTagTitle")}
                  </p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {t("targets.filterByTagHint")}
                  </p>
                </div>
                <div className="flex max-h-[220px] flex-col overflow-y-auto p-1.5">
                  {availableTags.map((tag) => {
                    const checked = draftTagFilters.includes(tag);
                    return (
                      <button
                        type="button"
                        key={tag}
                        className={cn(
                          "flex h-[34px] items-center gap-2 rounded-[8px] px-2 text-left text-xs font-medium text-foreground hover:bg-control-highlight",
                          checked && "bg-control-highlight",
                        )}
                        onClick={() =>
                          setDraftTagFilters((current) =>
                            current.includes(tag)
                              ? current.filter((item) => item !== tag)
                              : [...current, tag],
                          )
                        }
                      >
                        <Checkbox
                          checked={checked}
                          tabIndex={-1}
                          aria-hidden="true"
                          className="size-3.5 rounded-[4px] [&_[data-slot=checkbox-indicator]>svg]:size-2.5"
                        />
                        {tag}
                      </button>
                    );
                  })}
                </div>
                <div className="flex h-11 items-center justify-between border-t border-line bg-subtle px-3">
                  <Button
                    variant="ghost"
                    size="xs"
                    className="h-7 text-[11px]"
                    onClick={() => setDraftTagFilters([])}
                  >
                    {t("targets.filterClear")}
                  </Button>
                  <Button
                    size="xs"
                    className="h-7 text-[11px]"
                    onClick={() => {
                      setTagFilters(draftTagFilters);
                      setPage(1);
                      setTagFilterOpen(false);
                    }}
                  >
                    {t("targets.filterApply")}
                  </Button>
                </div>
              </PopoverPopup>
            </Popover>
          </div>
        </>
      }
      footer={
        <DataTablePagination
          page={currentPage}
          pageCount={pageCount}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 50]}
          summary={t("targets.pagination", {
            from: (currentPage - 1) * pageSize + (visible.length ? 1 : 0),
            to: (currentPage - 1) * pageSize + visible.length,
            total: sorted.length,
          })}
          onPageChange={setPage}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(nextPageSize);
            setPage(1);
          }}
        />
      }
      viewportClassName="max-h-[480px] overflow-auto"
    >
      <DataTable stickyHeader density="standard">
        <TableHeader>
          <TableRow>
            <SortHead
              label={t("targets.table.target")}
              active={sortKey === "label"}
              dir={sortDir}
              onClick={() => handleSort("label")}
            />
            <TableHead>{t("targets.table.tags")}</TableHead>
            <SortHead
              label={t("targets.table.bid")}
              active={sortKey === "bid"}
              dir={sortDir}
              onClick={() => handleSort("bid")}
              alignRight
            />
            <SortHead
              label={t("targets.table.spend")}
              active={sortKey === "spend"}
              dir={sortDir}
              onClick={() => handleSort("spend")}
              alignRight
            />
            <SortHead
              label={t("targets.table.sales")}
              active={sortKey === "sales"}
              dir={sortDir}
              onClick={() => handleSort("sales")}
              alignRight
            />
            <SortHead
              label={t("metrics.acos")}
              active={sortKey === "acos"}
              dir={sortDir}
              onClick={() => handleSort("acos")}
              alignRight
            />
            <SortHead
              label={t("targets.table.orders")}
              active={sortKey === "orders"}
              dir={sortDir}
              onClick={() => handleSort("orders")}
              alignRight
            />
            <TableHead className="text-right">{t("targets.table.latestAiAction")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visible.map((target) => {
            // Coverage is server-driven: a target without report rows in range
            // renders "—" rather than a fake 0, whatever its type.
            const hasMetrics = target.hasMetrics;
            return (
              <TableRow key={target.id}>
                <TableCell className="max-w-[280px]">
                  <p className="truncate text-xs font-semibold text-foreground">{target.label}</p>
                  <p className="mt-0.5 text-[10px] capitalize text-muted-foreground">
                    {target.targetType}
                    {target.keywordMatchType ? ` · ${target.keywordMatchType}` : ""}
                  </p>
                </TableCell>
                <TableCell className="min-w-[180px]">
                  <Popover
                    open={editingTargetId === target.id}
                    onOpenChange={(open) => {
                      if (open) startEdit(target);
                      else setEditingTargetId(null);
                    }}
                  >
                    <PopoverTrigger
                      aria-label={t("targets.editTagsAria")}
                      className="flex min-h-8 flex-wrap items-center gap-1.5 rounded-[8px] text-left outline-none focus-visible:ring-2 focus-visible:ring-ring [&[aria-expanded=true]_[data-slot=tag-add]]:bg-control-highlight"
                    >
                      {target.tags.map((tag) => (
                        <Badge
                          key={tag.tag}
                          variant="outline"
                          size="compact"
                          className="rounded-full"
                        >
                          {tag.tag}
                        </Badge>
                      ))}
                      <span
                        data-slot="tag-add"
                        className="flex size-[22px] shrink-0 items-center justify-center rounded-[8px] border border-control-border bg-control-default text-muted-foreground transition-[background-color,border-color,box-shadow] hover:bg-control-highlight"
                      >
                        <IconPlus className="size-2.5" aria-hidden />
                      </span>
                    </PopoverTrigger>
                    <PopoverPopup align="start" className="w-[280px] p-0">
                      <div className="border-b border-line px-3 py-2.5">
                        <p className="text-xs font-semibold">{t("targets.editTagsTitle")}</p>
                        <p className="mt-0.5 text-[10px] text-muted-foreground">
                          {t("targets.editTagsHint")}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-3">
                        <Input
                          value={draftTags}
                          onChange={(event) => setDraftTags(event.target.value)}
                          className="h-8 text-xs"
                          placeholder={t("targets.tagsPlaceholder")}
                        />
                        <Button
                          type="button"
                          size="sm"
                          aria-label={t("targets.saveTags")}
                          disabled={mutation.isPending}
                          onClick={() => saveTags(target.id)}
                        >
                          <IconDeviceFloppy className="size-3.5" /> {t("common:actions.save")}
                        </Button>
                      </div>
                    </PopoverPopup>
                  </Popover>
                </TableCell>
                <MetricCell
                  value={target.bid == null ? t("targets.na") : formatCurrency(target.bid)}
                />
                <PerformanceMetricCell
                  hasMetrics={hasMetrics}
                  value={formatCurrency(target.metrics.spend)}
                />
                <PerformanceMetricCell
                  hasMetrics={hasMetrics}
                  value={formatCurrency(target.metrics.sales)}
                />
                <PerformanceMetricCell
                  hasMetrics={hasMetrics}
                  value={formatPercent(target.metrics.acos)}
                />
                <PerformanceMetricCell
                  hasMetrics={hasMetrics}
                  value={formatNumber(target.metrics.orders)}
                />
                <TableCell className="text-right text-[10px] text-muted-foreground">
                  {target.risk === "high" || target.risk === "critical"
                    ? t("targets.latestAction.reviewProposed")
                    : t("targets.latestAction.monitoring")}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </DataTable>
    </DataTableFrame>
  );
}

export function TargetsSection({
  campaignId,
  targets,
}: {
  campaignId: string;
  targets: CampaignTarget[];
}) {
  const { t } = useTranslation("campaign");
  return (
    <section>
      {targets.length === 0 ? (
        <EmptyState
          icon={<IconBrain className="size-16" />}
          title={t("targets.emptyTitle")}
          description={t("targets.emptyDescription")}
        />
      ) : (
        <TargetsTableInner campaignId={campaignId} targets={targets} />
      )}
    </section>
  );
}

export function NegativesGroup({
  title,
  negatives,
  showAdGroup,
}: {
  title: string;
  negatives: CampaignNegativeTarget[];
  showAdGroup: boolean;
}) {
  const { t } = useTranslation("campaign");
  const [query, setQuery] = useState("");
  const filtered = negatives.filter((negative) =>
    negative.label.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <DataTableFrame
      toolbar={
        <>
          <div>
            <h3 className="text-xs font-semibold text-ink">{title}</h3>
            <p className="text-[9px] text-muted-foreground">
              {t("negatives.targetsCount", { total: negatives.length })}
            </p>
          </div>
          <SearchInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("negatives.searchPlaceholder")}
            className="w-[220px]"
          />
        </>
      }
      footer={
        <>
          <span>
            {t("negatives.showingSummary", {
              shown: Math.min(12, filtered.length),
              total: filtered.length,
            })}
          </span>
          <span>{t("negatives.protectionsCombined")}</span>
        </>
      }
      viewportClassName="max-h-[430px] overflow-auto"
    >
      <DataTable stickyHeader density="compact">
        <TableHeader>
          <TableRow>
            <TableHead>{t("negatives.table.negativeTarget")}</TableHead>
            <TableHead>{t("negatives.table.type")}</TableHead>
            {showAdGroup ? <TableHead>{t("negatives.table.adGroup")}</TableHead> : null}
            <TableHead>{t("negatives.table.status")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.slice(0, 12).map((negative) => (
            <TableRow key={negative.id}>
              <TableCell className="max-w-[260px] truncate font-medium text-foreground">
                {negative.label}
              </TableCell>
              <TableCell>
                <TargetTypeBadge targetType={negative.targetType} />
              </TableCell>
              {showAdGroup ? (
                <TableCell className="text-muted-foreground">{negative.adGroup ?? "—"}</TableCell>
              ) : null}
              <TableCell>
                <Badge variant="outline" className="capitalize">
                  {labelize(negative.status)}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </DataTable>
    </DataTableFrame>
  );
}

type SearchTermSortKey = "text" | "spend" | "sales" | "acos" | "orders" | "cvr";

/**
 * AI Suggestion badge tones (ADP-21): semantic colors, not brand — harvest is
 * a positive signal, negative maps to destructive, observe stays muted.
 */
const SUGGESTION_STYLES: Record<CampaignSearchTerm["aiSuggestion"], string> = {
  harvest: "border-success/30 bg-success/10 text-success-foreground",
  negative: "border-destructive/30 bg-destructive/10 text-destructive-foreground",
  observe: "border-muted-foreground/30 bg-muted text-muted-foreground",
};

function searchTermSortValue(term: CampaignSearchTerm, key: SearchTermSortKey): string | number {
  if (key === "text") return term.text;
  return term.metrics[key];
}

export function SearchTermsTable({ searchTerms }: { searchTerms: CampaignSearchTerm[] }) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatPercent, formatNumber } = useFormatters();
  const [sortKey, setSortKey] = useState<SearchTermSortKey>("spend");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [query, setQuery] = useState("");
  const [outcome, setOutcome] = useState<SearchTermOutcome>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const sorted = useMemo(() => {
    return searchTerms
      .filter(
        (term) =>
          term.text.toLowerCase().includes(query.toLowerCase()) &&
          (outcome === "all" || term.aiSuggestion === outcome),
      )
      .sort((a, b) => {
        const av = searchTermSortValue(a, sortKey);
        const bv = searchTermSortValue(b, sortKey);
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortDir === "asc" ? cmp : -cmp;
      });
  }, [searchTerms, query, sortKey, sortDir, outcome]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const visible = sorted.slice((page - 1) * pageSize, page * pageSize);

  function handleSort(key: SearchTermSortKey) {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("desc");
  }

  return (
    <DataTableFrame
      toolbar={
        <>
          <SearchInput
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder={t("searchTerms.searchPlaceholder")}
            className="w-[220px]"
          />
          <FilterSelect
            ariaLabel={t("searchTerms.filterOutcomeAria")}
            items={SEARCH_TERM_OUTCOME_VALUES.map((value) => ({
              value,
              label: t(`searchTerms.outcomeFilter.${value}`),
            }))}
            value={outcome}
            onValueChange={(next) => {
              setOutcome(next as SearchTermOutcome);
              setPage(1);
            }}
          />
        </>
      }
      footer={
        <DataTablePagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 50]}
          summary={t("searchTerms.pagination", {
            from: (page - 1) * pageSize + (visible.length ? 1 : 0),
            to: (page - 1) * pageSize + visible.length,
            total: sorted.length,
          })}
          onPageChange={setPage}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(nextPageSize);
            setPage(1);
          }}
        />
      }
      viewportClassName="max-h-[480px] overflow-auto"
    >
      <DataTable stickyHeader density="standard">
        <TableHeader>
          <TableRow>
            <SortHead
              label={t("searchTerms.columns.searchTerm")}
              active={sortKey === "text"}
              dir={sortDir}
              onClick={() => handleSort("text")}
            />
            <TableHead>{t("searchTerms.columns.matchedTarget")}</TableHead>
            <TableHead>{t("searchTerms.columns.matchType")}</TableHead>
            <SortHead
              label={t("searchTerms.table.spend")}
              active={sortKey === "spend"}
              dir={sortDir}
              onClick={() => handleSort("spend")}
              alignRight
            />
            <SortHead
              label={t("searchTerms.table.sales")}
              active={sortKey === "sales"}
              dir={sortDir}
              onClick={() => handleSort("sales")}
              alignRight
            />
            <SortHead
              label={t("metrics.acos")}
              active={sortKey === "acos"}
              dir={sortDir}
              onClick={() => handleSort("acos")}
              alignRight
            />
            <SortHead
              label={t("searchTerms.table.orders")}
              active={sortKey === "orders"}
              dir={sortDir}
              onClick={() => handleSort("orders")}
              alignRight
            />
            <TableHead>{t("searchTerms.columns.aiSuggestion")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visible.map((term) => (
            <TableRow key={term.id}>
              <TableCell className="font-medium text-foreground">{term.text}</TableCell>
              <TableCell className="text-muted-foreground">
                {term.matchedTarget ?? t("searchTerms.na")}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs capitalize">
                  {searchTermMatchLabel(term, t)}
                </Badge>
              </TableCell>
              <MetricCell value={formatCurrency(term.metrics.spend)} />
              <MetricCell value={formatCurrency(term.metrics.sales)} />
              <MetricCell value={formatPercent(term.metrics.acos)} />
              <MetricCell value={formatNumber(term.metrics.orders)} />
              <TableCell>
                <Badge
                  variant="outline"
                  className={cn("text-xs capitalize", SUGGESTION_STYLES[term.aiSuggestion])}
                >
                  {labelize(term.aiSuggestion)}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </DataTable>
    </DataTableFrame>
  );
}

export function AiActionsTable({ actions }: { actions: CampaignAiAction[] }) {
  const { t } = useTranslation("campaign");
  const { formatDateTime } = useFormatters();
  if (actions.length === 0) {
    return (
      <EmptyState
        icon={<IconBrain className="size-16" />}
        title={t("aiActions.emptyTitle")}
        description={t("aiActions.emptyDescription")}
      />
    );
  }

  return (
    <DataTableFrame viewportClassName="max-h-[480px] overflow-auto">
      <DataTable stickyHeader density="standard">
        <TableHeader>
          <TableRow>
            <TableHead>{t("aiActions.table.action")}</TableHead>
            <TableHead>{t("aiActions.table.target")}</TableHead>
            <TableHead>{t("aiActions.table.status")}</TableHead>
            <TableHead>{t("aiActions.table.reason")}</TableHead>
            <TableHead>{t("aiActions.table.created")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {actions.map((action) => (
            <TableRow key={action.id}>
              <TableCell className="font-medium text-foreground">
                {labelize(action.actionType)}
              </TableCell>
              <TableCell>{action.target}</TableCell>
              <TableCell>
                <Badge variant="outline">{labelize(action.status)}</Badge>
              </TableCell>
              <TableCell className="max-w-[420px] truncate text-muted-foreground">
                {action.reason}
              </TableCell>
              <TableCell className="font-mono text-xs tabular-nums">
                {formatDateTime(action.createdAt)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </DataTable>
    </DataTableFrame>
  );
}

function SortHead({
  label,
  active,
  dir,
  onClick,
  alignRight = false,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  alignRight?: boolean;
}) {
  return (
    <TableHead
      className={cn("cursor-pointer select-none", alignRight && "text-right")}
      onClick={onClick}
    >
      <span className={cn("inline-flex items-center gap-1", alignRight && "justify-end")}>
        {label}
        {active ? (
          <IconArrowUp className={cn("size-3", dir === "desc" && "rotate-180")} />
        ) : (
          <IconArrowsUpDown className="size-3 opacity-30" />
        )}
      </span>
    </TableHead>
  );
}

function MetricCell({ value }: { value: string }) {
  return <TableCell className="text-right font-mono tabular-nums">{value}</TableCell>;
}

function targetSortValue(target: CampaignTarget, key: SortKey): string | number {
  if (key === "label") return target.label;
  if (key === "matchType") return target.keywordMatchType ?? "";
  if (key === "adGroup") return target.adGroup;
  if (key === "bid") return target.bid ?? 0;
  return target.metrics[key];
}

/** Auto expressions → human name; keyword → match type; else em-dash (D2/D6). */
function searchTermMatchLabel(
  term: CampaignSearchTerm,
  t: (key: string, options?: { defaultValue: string }) => string,
): string {
  if (term.expressionType) {
    return t(`expression.${term.expressionType}`, {
      defaultValue: expressionTypeLabel(term.expressionType) ?? labelize(term.expressionType),
    });
  }
  if (term.keywordMatchType) return term.keywordMatchType;
  return "—";
}

function TargetTypeBadge({ targetType }: { targetType: TargetType }) {
  return (
    <Badge variant="outline" className="text-xs capitalize">
      {labelize(targetType)}
    </Badge>
  );
}

/** Per-target metrics only exist for keyword targets (ADP-39 D6); others show —. */
function PerformanceMetricCell({ hasMetrics, value }: { hasMetrics: boolean; value: string }) {
  const { t } = useTranslation("campaign");
  if (!hasMetrics) {
    return (
      <TableCell
        className="cursor-help text-right font-mono tabular-nums text-muted-foreground"
        title={t("targets.metricsPending")}
      >
        —
      </TableCell>
    );
  }
  return <TableCell className="text-right font-mono tabular-nums">{value}</TableCell>;
}

export function labelize(value: string): string {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
